import { Typography } from '@mui/material';
import { FormItemDoc } from '../../inputs/_input.types';
import debounce from 'lodash.debounce';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { mbToNumUtf16Chars } from '../../../libTs/depless';
import {
  coerceAutosaveList,
  generateStorageUsageMessage,
  isQuotaExceededError,
  tryPersistAutosaves,
} from './autosaveUtils';
import {
  clearAllAutosaveStorage,
  loadAutosaveStorage,
  saveAutosaveStorage,
} from './autosaveDb';
import {
  logAutosaveHazardEvent,
  logAutosaveHazardEventOnce,
} from './logAutosaveHazardEvent';
import {
  LOCAL_STORAGE_AUTOSAVES_KEY,
  pruneLocalStorageAutosaves,
} from './localStoragePrune';
import { AutosaveTypedConfirmDialog } from './AutosaveTypedConfirmDialog';

// 4.5 MB (bytes) = 2.25M UTF-16 chars — under even Safari's ~2.5M-char
// localStorage quota. A Words & Pictures draft carries its pictures inline
// (~1.5-2 MB of JSON), so two drafts filled the old 3 MB budget and every
// further note triggered the delete-autosaves dialog. This limit now only
// governs the localStorage FALLBACK mode (no IndexedDB available).
export const LOCAL_STORAGE_APP_LIMIT_MB = 4.5;
export const LOCAL_STORAGE_APP_LIMIT_UTF16_CHARS = mbToNumUtf16Chars(
  LOCAL_STORAGE_APP_LIMIT_MB
);
// IndexedDB mode (the normal case — see docs/specs/autosave-indexeddb-
// migration.md): the budget is effectively unbounded; this keeps the
// eviction machinery as a far-above-real-use backstop.
export const IDB_APP_LIMIT_MB = 150;
// Tiny localStorage key bumped after every IndexedDB persist — IDB has no
// cross-tab change events, so other tabs listen for this and re-read.
const AUTOSAVE_PING_KEY = 'autosaves_ping';
// Cross-tab broadcast for a device-wide draft disposal (sgdev card `ymp`).
// It cannot be folded into the ping key: a ping only means "re-read storage",
// and after a wipe a sibling tab that re-reads still holds the whole draft
// list in memory — its next persist would write that PHI straight back into
// the store we just emptied. Hearing THIS key makes a sibling drop its own
// state, which is the only thing that makes the wipe stick.
export const AUTOSAVE_WIPE_KEY = 'autosaves_wipe';

export type AutosaveT = {
  id: string;
  timeCreated: number; // When the autosave was created.
  timeUpdated: number; // Last update timestamp.
  // The form values the draft holds — a soup note's form document, the same
  // `FormItemDoc` shape `SoupNoteForm` carries as `formData` and hands to
  // `CompactAutosave` as `val`. `null` is the tombstone state: permanent
  // deletion nulls the content and keeps the record.
  data: FormItemDoc | null;
  pathname: string; // Associated URL path.
  subpath: string; // Identifier for which form this autosave belongs to.
  trashedAt?: number; // When the autosave was trashed (i.e. soft-deleted).
  permanentlyDeletedAt?: number; // Tombstone marker for permanent deletion.
};

// Outcome of the one-time request that the browser exempt this origin's storage
// from automatic eviction under disk pressure. 'unsupported' covers both a
// browser without the Storage API and a request that threw (Safari private
// windows). A grant is not permanent: clearing browsing data revokes it.
export type StoragePersistenceT =
  | 'pending'
  | 'granted'
  | 'denied'
  | 'unsupported';

// Set when the last persist attempt failed (e.g. storage quota full) — covers
// both the IndexedDB primary path and the localStorage fallback. Cleared
// automatically by the next successful persist.
export type AutosavePersistFailureT = {
  at: number;
  quotaExceeded: boolean;
};

type AutosaveContextValue = {
  autosaves: AutosaveT[];
  // Non-null while drafts are NOT reaching storage (IndexedDB primary or the
  // localStorage fallback) — drive save indicators from this so a failed
  // persist shows as "not saved", never as a silent success or an uncaught
  // exception.
  persistFailure: AutosavePersistFailureT | null;
  addAutosave: (autosave: AutosaveT) => void;
  // Archive (soft-delete) an autosave. Retains its data — see restoreAutosave.
  trashAutosave: (id: string) => void;
  // Undo a trash, returning the draft to the active list with its data intact.
  restoreAutosave: (id: string) => void;
  // Outcome of the one-time navigator.storage.persist() request.
  storagePersistence: StoragePersistenceT;
  updateAutosave: (id: string, newData: FormItemDoc) => void;
  debouncedUpdateAutosave: (id: string, newData: FormItemDoc) => void;
  trashAllAutosavesMaybe: () => void;
  // Permanently delete an autosave (leaves a tombstone to be purged later).
  // Pass the exact confirmation string to bypass the prompt
  deleteAutosavePermanently: (id: string, bypassConfirmation?: string) => void;
  // Trash and permanently delete an autosave atomically (no confirmation).
  trashAndDeleteAutosavePermanently: (id: string) => void;
  // Clear all tombstone records (permanently deleted autosave IDs)
  clearDeletedAutosaveIds: () => void;
  // Destroy every draft this device holds — active, trashed and tombstoned —
  // in memory, in IndexedDB and in the legacy localStorage key, and tell every
  // other open tab to drop its copy.
  //
  // EXPLICIT, CASEWORKER-INITIATED ONLY. Nothing may call this from a
  // lifecycle event: sgdev card `ymp` entry `e03` rejects wiping on logout or
  // session expiry outright, because a worker whose session timed out has not
  // chosen to lose the draft they were writing.
  //
  // Rejects if storage could not be cleared, so the caller can say so rather
  // than report a disposal that did not happen.
  removeAllDraftsFromThisDevice: () => Promise<void>;
};

const AutosaveContext = createContext<AutosaveContextValue | undefined>(
  undefined
);

// --- Utility Functions ---

// Transform an autosave object to trash and mark for permanent deletion
const trashAndDeleteAutosaveObj = (autosave: AutosaveT): AutosaveT => ({
  ...autosave,
  data: null,
  trashedAt: autosave.trashedAt || Date.now(),
  permanentlyDeletedAt: autosave.permanentlyDeletedAt || Date.now(),
});

// Calculate size of all localStorage items except 'autosaves'
const calculateLocalStorageSize = () => {
  return Object.keys(localStorage).reduce((total, key) => {
    if (key !== 'autosaves') {
      const item = localStorage.getItem(key);
      return total + (item ? item.length : 0);
    }
    return total;
  }, 0);
};

// Categorize localStorage items for usage reporting.
const categorizeLocalStorageItems = () => {
  return Object.keys(localStorage).reduce(
    (acc, key) => {
      const item = localStorage.getItem(key);
      const size = item ? item.length : 0;
      if (key === 'autosaves') {
        acc.gen2Autosaves += size;
      } else if (key === 'drafts') {
        acc.gen1Autosaves += size;
      } else {
        acc.other += size;
      }
      return acc;
    },
    { gen2Autosaves: 0, gen1Autosaves: 0, other: 0 }
  );
};

// The exact phrase that unlocks the storage-full eviction. `DELETE` is the
// same word `deleteAutosavePermanently` demands for destroying one draft —
// this path destroys several at once, so it cannot be guarded more weakly.
export const EVICTION_CONFIRMATION_PHRASE = 'DELETE';

// A storage-full eviction waiting on the caseworker's typed confirmation.
// Held as data, never as a half-applied state change: until it is confirmed,
// every draft it names is still whole.
export type PendingEvictionT = {
  /** Trashed drafts to tombstone, oldest first. */
  evictIds: string[];
  /** Plain-language body for the typed-confirm dialog. */
  message: string;
};

// Pure: work out which TRASHED drafts would have to be destroyed for the new
// draft to fit, and word the prompt. Returns null when everything fits, and
// null when the trash cannot free enough — the ladder never climbs to a live
// draft (card `8e1` entry `e17`); over-budget with an empty trash is left to
// fail the persist and raise the "Draft not saved" banner instead.
export function planStorageEviction(
  prevAutosaves: AutosaveT[],
  newAutosave: AutosaveT,
  limitChars: number
): PendingEvictionT | null {
  const otherLocalStorageSize = calculateLocalStorageSize();
  let candidateState = [...prevAutosaves, newAutosave];
  let candidateJson = JSON.stringify(candidateState);

  if (otherLocalStorageSize + candidateJson.length <= limitChars) return null;

  // Clearing the existing tombstones is the free rung — they hold no content.
  candidateState = candidateState.filter((a) => !a.permanentlyDeletedAt);
  candidateJson = JSON.stringify(candidateState);

  const removedAutosaves: AutosaveT[] = [];
  while (otherLocalStorageSize + candidateJson.length > limitChars) {
    const removable = candidateState.filter(
      (a) => a.trashedAt && !a.permanentlyDeletedAt
    );
    if (removable.length === 0) break;

    const oldest = removable.reduce((prev, curr) => {
      const prevTime = prev.trashedAt || prev.timeUpdated;
      const currTime = curr.trashedAt || curr.timeUpdated;
      return prevTime < currTime ? prev : curr;
    });
    removedAutosaves.push(oldest);

    candidateState = candidateState.map((a) =>
      a.id === oldest.id ? trashAndDeleteAutosaveObj(a) : a
    );
    candidateJson = JSON.stringify(candidateState);
  }

  if (removedAutosaves.length === 0) return null;

  const plural = removedAutosaves.length > 1;
  const trashedDates = removedAutosaves
    .map((a) => new Date(a.trashedAt!).toLocaleString())
    .join(', ');

  const message =
    `This device's storage space for drafts is full. To keep saving your work here, ` +
    `we would have to clear ${removedAutosaves.length} draft${plural ? 's' : ''} ` +
    `already in the trash (moved there ${trashedDates}).\n\n` +
    `${plural ? 'Those drafts' : 'That draft'} still hold${plural ? '' : 's'} everything ` +
    `you wrote in ${plural ? 'them' : 'it'}. Clearing ${plural ? 'them' : 'it'} here deletes ` +
    `that for good — ${plural ? 'they' : 'it'} cannot be restored afterwards.\n\n` +
    `Cancel keeps ${plural ? 'them' : 'it'}, deletes nothing, and leaves you on this page ` +
    `with your work as it is. The draft you are writing now just will not be saved to this ` +
    `device, and the form will say so.\n\n` +
    generateStorageUsageMessage(categorizeLocalStorageItems());

  return { evictIds: removedAutosaves.map((a) => a.id), message };
}

// Returns the effective update time for a record. INVARIANT: every mutation
// must raise this number, because mergeAutosaves resolves cross-tab conflicts
// by keeping the later one — a mutation that lowers it is silently undone by
// the next merge with another tab. Clearing a marker therefore has to bump
// `timeUpdated` (see restoreAutosave), the way updateAutosave always has.
const getEffectiveUpdateTime = (record: AutosaveT): number =>
  Math.max(
    record.timeUpdated || 0,
    record.trashedAt || 0,
    record.permanentlyDeletedAt || 0
  );

// Drop every record that predates a device-wide disposal. Applied to INCOMING
// arrays only (cold-start load, ping-triggered re-read, legacy storage event)
// and never to locally-created state: `mergeAutosaves` is a union that never
// subtracts, so without this cutoff an IndexedDB read already in flight when
// the wipe landed merges the whole pre-wipe list straight back in. A record
// touched AFTER the wipe still merges normally — this is a cutoff, not a mute.
export const dropRecordsWipedAt = (
  records: AutosaveT[],
  wipeAt: number
): AutosaveT[] =>
  wipeAt ? records.filter((r) => getEffectiveUpdateTime(r) > wipeAt) : records;

// The watermark is PERSISTED, not memory-only. A sibling tab's persist already
// in flight when the wipe landed can rewrite the pre-wipe drafts into storage
// after the clear; a watermark that reset to 0 on reload would then merge that
// content straight back in on the next cold start, in every tab, with nothing
// filtering it. Reading the same key the wipe already writes closes that.
export const readStoredDeviceWipeAt = (): number => {
  try {
    const parsed = Number(localStorage.getItem(AUTOSAVE_WIPE_KEY));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
};

// Merge two autosave arrays by id, favoring the record with the later effective update time.
const mergeAutosaves = (
  current: AutosaveT[],
  incoming: AutosaveT[]
): AutosaveT[] => {
  const merged: { [id: string]: AutosaveT } = {};

  // Add all current records.
  current.forEach((record) => {
    merged[record.id] = record;
  });

  // Merge incoming records.
  incoming.forEach((record) => {
    if (merged[record.id]) {
      const existing = merged[record.id];
      const existingTime = getEffectiveUpdateTime(existing);
      const incomingTime = getEffectiveUpdateTime(record);
      if (incomingTime > existingTime) {
        merged[record.id] = record;
      } else if (
        incomingTime === existingTime &&
        JSON.stringify(existing) !== JSON.stringify(record)
      ) {
        console.error(
          `Conflict detected for autosave ${record.id}: equal update times with differing data.`
        );
      }
    } else {
      merged[record.id] = record;
    }
  });

  return Object.values(merged);
};

type AutosaveStorage = {
  autosaves: AutosaveT[];
  lastTombstoneClearTime?: number;
};

export const AutosaveProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Read once per mount, before any state is seeded: every incoming array in
  // this provider — including the synchronous localStorage seed below — is
  // filtered against the last device-wide disposal this origin recorded.
  const [initialDeviceWipeAt] = useState<number>(readStoredDeviceWipeAt);

  // Initialize state from localStorage.
  const [autosaves, setAutosaves] = useState<AutosaveT[]>(() => {
    const stored = localStorage.getItem('autosaves');
    if (!stored) return [];

    try {
      const parsed = JSON.parse(stored);
      // Handle legacy format (just array) vs new format (object with metadata).
      // coerceAutosaveList guards against a malformed non-array (e.g. {autosaves:{}}).
      return dropRecordsWipedAt(coerceAutosaveList(parsed), initialDeviceWipeAt);
    } catch {
      return [];
    }
  });

  const [lastTombstoneClearTime, setLastTombstoneClearTime] = useState<number>(() => {
    const stored = localStorage.getItem('autosaves');
    if (!stored) return 0;

    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? 0 : parsed.lastTombstoneClearTime || 0;
    } catch {
      return 0;
    }
  });

  // Timestamp of the most recent device-wide draft disposal this origin has
  // recorded (0 = none ever). Every INCOMING array is filtered against it —
  // see `dropRecordsWipedAt`. Seeded from `localStorage[AUTOSAVE_WIPE_KEY]`
  // so the cutoff survives a reload: a stale write that landed after the
  // clear must not come back on a cold start.
  const deviceWipeAtRef = useRef<number>(initialDeviceWipeAt);

  // IndexedDB is the primary store; localStorage (read synchronously above)
  // is the migration source AND the fallback for environments without IDB
  // (decided optimistically here; the first failed load flips it off — Safari
  // private windows can throw on open, not just lack the global).
  const [idbMode, setIdbMode] = useState<boolean>(
    () => typeof indexedDB !== 'undefined'
  );
  // Children are held until the initial IDB read lands (ms-scale, once, at
  // app root) — without this a fast-mounting form could look for its draft
  // before the store is hydrated and silently miss it.
  const [idbReady, setIdbReady] = useState<boolean>(false);
  useEffect(() => {
    if (!idbMode) return undefined;
    let cancelled = false;
    loadAutosaveStorage()
      .then((stored) => {
        if (cancelled) return;
        if (stored) {
          const incoming = dropRecordsWipedAt(
            Array.isArray(stored.autosaves)
              ? (stored.autosaves as AutosaveT[])
              : [],
            deviceWipeAtRef.current
          );
          const incomingClearTime = stored.lastTombstoneClearTime || 0;
          setAutosaves((current) => mergeAutosaves(current, incoming));
          setLastTombstoneClearTime((current) =>
            Math.max(current, incomingClearTime)
          );
        }
        setIdbReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(
          'Autosave IndexedDB unavailable — falling back to localStorage:',
          err
        );
        // Failure path only (card `8e1` entry `e18`): this browser has fallen
        // off the primary store onto the 4.5 MB localStorage budget for the
        // whole session, which is the mode where the eviction ladder and the
        // quota wall are actually reachable. How often it happens in the field
        // is otherwise unobservable. Nothing fires when IndexedDB opens.
        logAutosaveHazardEvent(
          'indexeddb-unavailable',
          'IndexedDB could not be opened for drafts. This session fell back to the much smaller localStorage store for the rest of its life.',
          { phase: 'cold-start-open' }
        );
        setIdbMode(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idbMode]);

  // Ask the browser not to evict this origin's storage under disk pressure.
  // Drafts live only in the browser, so an eviction is the one draft-loss path
  // a caseworker can neither cause nor be talked out of. This is a request, not
  // a guarantee — Chrome decides silently from engagement signals and Firefox
  // prompts — so check persisted() first and never re-ask a granted origin.
  // The outcome is a console line, not telemetry; the grant RATE needs the
  // batched /log-event sink, which is a separate follow-up (card `aam`).
  // `storagePersistence` is exposed for the drafts surface that card `fll`
  // specifies; nothing consumes it yet.
  const [storagePersistence, setStoragePersistence] =
    useState<StoragePersistenceT>('pending');
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) {
      setStoragePersistence('unsupported');
      return undefined;
    }
    let cancelled = false;
    navigator.storage
      .persisted()
      .then((already) => (already ? true : navigator.storage.persist()))
      .then((granted) => {
        if (cancelled) return;
        setStoragePersistence(granted ? 'granted' : 'denied');
        console.log(
          `Autosave storage persistence: ${granted ? 'granted' : 'denied'}`
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setStoragePersistence('unsupported');
        console.error('Autosave storage persistence request failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Subscribe to localStorage changes from other tabs.
  useEffect(() => {
    const onStorageChange = (event: StorageEvent) => {
      // Another tab ran a device-wide disposal. Drop this tab's copy of the
      // drafts so its next persist cannot write them back into the store the
      // other tab just emptied. Storage is the wiping tab's to clear — this
      // branch never clears storage and never re-broadcasts.
      if (event.key === AUTOSAVE_WIPE_KEY) {
        const wipeAt = Number(event.newValue);
        if (!Number.isFinite(wipeAt) || wipeAt <= deviceWipeAtRef.current) {
          return;
        }
        deviceWipeAtRef.current = wipeAt;
        setAutosaves([]);
        setLastTombstoneClearTime(wipeAt);
        return;
      }
      // IDB mode: another tab persisted and bumped the ping key — re-read
      // the shared store and run the same merge as the legacy path below.
      if (event.key === AUTOSAVE_PING_KEY) {
        loadAutosaveStorage()
          .then((stored) => {
            if (!stored) return;
            const incomingAutosaves = dropRecordsWipedAt(
              Array.isArray(stored.autosaves)
                ? (stored.autosaves as AutosaveT[])
                : [],
              deviceWipeAtRef.current
            );
            const incomingClearTime = stored.lastTombstoneClearTime || 0;
            setAutosaves((current) => mergeAutosaves(current, incomingAutosaves));
            setLastTombstoneClearTime((currentClearTime) => {
              if (incomingClearTime > currentClearTime) {
                setAutosaves((prev) =>
                  prev.filter((autosave) => !autosave.permanentlyDeletedAt)
                );
              }
              return Math.max(currentClearTime, incomingClearTime);
            });
          })
          .catch(() => {
            /* other tab's write will ping again */
          });
        return;
      }
      if (event.key === 'autosaves') {
        if (!event.newValue) return;

        try {
          const parsed = JSON.parse(event.newValue);
          const incomingAutosaves = dropRecordsWipedAt(
            coerceAutosaveList(parsed),
            deviceWipeAtRef.current
          );
          const incomingClearTime = Array.isArray(parsed) ? 0 : parsed.lastTombstoneClearTime || 0;

          setAutosaves((current) => mergeAutosaves(current, incomingAutosaves));

          // Update clear time if incoming is more recent
          setLastTombstoneClearTime((currentClearTime) => {
            const newClearTime = Math.max(currentClearTime, incomingClearTime);

            // If incoming clear time is newer, remove tombstones from current state
            if (incomingClearTime > currentClearTime) {
              setAutosaves((prev) => prev.filter((autosave) => !autosave.permanentlyDeletedAt));
            }

            return newClearTime;
          });
        } catch (error) {
          console.error('Error parsing autosaves from storage event:', error);
        }
      }
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, []);

  // Track the last persisted value to prevent cross-tab feedback loops
  const lastPersistedRef = useRef<string>('');

  // Latest-value ref so addAutosave can plan an eviction against the current
  // list without doing that work (or firing a dialog) inside a state updater.
  const autosavesRef = useRef<AutosaveT[]>(autosaves);
  autosavesRef.current = autosaves;

  // A storage-full eviction awaiting the caseworker's typed confirmation.
  const [pendingEviction, setPendingEviction] =
    useState<PendingEvictionT | null>(null);

  // Non-null while the latest persist attempt failed; mirrored in a ref so the
  // persist effect can detect recovery without adding itself as a dependency.
  const [persistFailure, setPersistFailure] =
    useState<AutosavePersistFailureT | null>(null);
  const persistFailureRef = useRef<AutosavePersistFailureT | null>(null);

  // Persist autosaves whenever they change — to IndexedDB (primary) or, in
  // fallback mode, localStorage. Either medium can fail: localStorage.setItem
  // throws synchronously on QuotaExceededError (most often mid-typing on the
  // UPDATE path, which the eviction ladder in addAutosave never sees), and an
  // IDB write can abort at commit (also quota). Both routes funnel through
  // onPersistSuccess / onPersistFailure so a failure is caught, counted, and
  // surfaced via persistFailure (save indicator shows "not saved") instead of
  // crashing the tree or silently diverging state from storage.
  useEffect(() => {
    const storage: AutosaveStorage = {
      autosaves,
      lastTombstoneClearTime: lastTombstoneClearTime || undefined,
    };
    const serialized = JSON.stringify(storage);

    // Only persist if the data actually changed — prevents cross-tab sync
    // loops where mergeAutosaves creates new array references.
    if (serialized === lastPersistedRef.current) return;

    // Shared accounting so the IDB and localStorage paths surface a failed
    // persist identically, and a later success clears it.
    const onPersistSuccess = () => {
      lastPersistedRef.current = serialized;
      if (persistFailureRef.current) {
        persistFailureRef.current = null;
        setPersistFailure(null);
      }
    };
    const onPersistFailure = (quotaExceeded: boolean, error: unknown) => {
      // Leave lastPersistedRef stale so the next change retries the write.
      // Log for support / error-reporting diagnosis. The raw error
      // distinguishes quota vs. SecurityError vs. blocked-open, which the
      // boolean flag alone drops. This `console.error` is the only part of the
      // failure path that is a console line — the `logAutosaveHazardEventOnce`
      // call just below DOES post to /log-event, once per session. What is
      // still a follow-up is the BATCHED sink (card `aam`), which is what a
      // rate (how often devices hit this) would need; one latched event per
      // session is not that.
      console.error('Autosave persist failed:', error);
      // Failure path only (card `8e1` entry `e18`). A mid-session persist
      // failure leaves the app in IndexedDB mode retrying forever, so the
      // cold-start event above never fires for it — without this, the more
      // likely half of draft-loss risk is invisible. Counts and verdicts
      // only: no draft content, no ids, no pathnames.
      // ONCE per session, not once per failure. This effect is driven by the
      // 500ms `debouncedUpdateAutosave`, so a device with wedged storage fails
      // again on every debounce tick and the unlatched helper would POST for
      // the rest of the session. The server's `dedupe: true` only suppresses
      // the WRITE, inside a 60s window, inside one gunicorn worker. See
      // `logAutosaveHazardEventOnce`.
      logAutosaveHazardEventOnce(
        'draft-persist-failed',
        'A draft could not be written to browser storage. The form shows the "not saved" indicator and the draft exists only in memory until a later write succeeds.',
        { mode: idbMode ? 'indexeddb' : 'localstorage', quota_exceeded: quotaExceeded }
      );
      const failure: AutosavePersistFailureT = {
        at: Date.now(),
        quotaExceeded,
      };
      persistFailureRef.current = failure;
      setPersistFailure(failure);
    };

    if (idbMode) {
      // Never persist before the initial load lands — it would clobber the
      // IDB store with the pre-hydration (possibly empty) state.
      if (!idbReady) return;
      saveAutosaveStorage(storage)
        .then(() => {
          onPersistSuccess();
          // Prune-only: new content never goes to localStorage any more, but
          // the legacy key is re-read on every mount and mergeAutosaves never
          // subtracts, so a frozen copy resurrects deleted drafts. Drop what
          // IndexedDB now holds an equal-or-newer copy of and remove the key
          // once it empties. AFTER the commit above, never before — pruning
          // on an uncommitted write could drop the only copy of a legacy
          // draft. See localStoragePrune.ts and card `8e1` entry `e17`.
          pruneLocalStorageAutosaves(autosaves);
          try {
            // Wake other tabs (IDB has no change events).
            localStorage.setItem(AUTOSAVE_PING_KEY, String(Date.now()));
          } catch {
            /* non-fatal */
          }
        })
        .catch((err) => {
          // Surface the IDB persist failure the same way as localStorage —
          // before, this was a bare console.error and the UI kept showing
          // "saved" while the write had rolled back.
          onPersistFailure(isQuotaExceededError(err), err);
        });
    } else {
      const result = tryPersistAutosaves(serialized);
      if (result.ok) {
        onPersistSuccess();
      } else {
        onPersistFailure(result.quotaExceeded, result.error);
      }
    }
  }, [autosaves, lastTombstoneClearTime, idbMode, idbReady]);

  // --- Autosave Operations ---

  // Add a new autosave, ensuring we don't exceed storage limits.
  //
  // The storage-full branch is ASYNCHRONOUS now: it can no longer decide
  // anything inside the state updater, because the caseworker answers in a
  // React dialog that resolves turns later. So the updater does the one thing
  // it is allowed to do — append the new draft — and the eviction plan is
  // handed to `pendingEviction`, which renders the typed-confirm dialog.
  // Nothing is destroyed until that confirmation is typed and accepted.
  const addAutosave = useCallback(
    (autosave: AutosaveT) => {
      const prevAutosaves = autosavesRef.current;
      if (prevAutosaves.some((a) => a.id === autosave.id)) {
        throw new Error('An autosave with that ID already exists');
      }

      const newAutosave: AutosaveT = {
        ...autosave,
        trashedAt: undefined,
        permanentlyDeletedAt: undefined,
      };

      // In IndexedDB mode the budget is a far-above-real-use backstop; the
      // tight limit only applies in the localStorage fallback.
      const limitMb = idbMode ? IDB_APP_LIMIT_MB : LOCAL_STORAGE_APP_LIMIT_MB;
      const plan = planStorageEviction(
        prevAutosaves,
        newAutosave,
        mbToNumUtf16Chars(limitMb)
      );

      // The draft itself always lands in memory. If storage cannot take it,
      // the persist fails and the red "Draft not saved" banner says so — the
      // caseworker keeps both the page and the words on it either way.
      setAutosaves((prev) =>
        prev.some((a) => a.id === newAutosave.id) ? prev : [...prev, newAutosave]
      );

      if (plan) setPendingEviction(plan);
    },
    [idbMode]
  );

  // The caseworker typed the confirmation: drop the tombstones and tombstone
  // the trashed drafts the plan named, in one state transition.
  const confirmPendingEviction = useCallback(() => {
    const plan = pendingEviction;
    setPendingEviction(null);
    if (!plan) return;
    const doomed = new Set(plan.evictIds);
    setAutosaves((prev) =>
      prev
        .filter((a) => !a.permanentlyDeletedAt)
        .map((a) => (doomed.has(a.id) ? trashAndDeleteAutosaveObj(a) : a))
    );
  }, [pendingEviction]);

  // Declining destroys nothing and — the whole point of card `fll`'s fix —
  // navigates nowhere. The Cancel branch used to call window.history.back(),
  // so saying "no" to a deletion threw the caseworker off the page they were
  // writing on. Now the trash is left alone, the new draft stays in memory,
  // and the failing persist is what tells them it is not saved.
  const cancelPendingEviction = useCallback(() => {
    setPendingEviction(null);
  }, []);

  // Update an autosave with new data and “revive” it by clearing any trash or permanent deletion markers.
  const updateAutosave = useCallback((id: string, newData: FormItemDoc) => {
    setAutosaves((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              data: newData,
              timeUpdated: Date.now(),
              trashedAt: undefined,
              permanentlyDeletedAt: undefined,
            }
          : a
      )
    );
  }, []);

  // Archive (soft-delete) an autosave by setting trashedAt. The draft's `data`
  // is RETAINED so trash is a real recycle bin: content survives until the
  // caseworker permanently deletes it (which does null `data`, behind a typed
  // confirm). Nulling here made every "delete" on the drafts surfaces
  // unrecoverable at stage one, which is not what their wording promises.
  const trashAutosave = useCallback((id: string) => {
    setAutosaves((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              trashedAt: a.trashedAt || Date.now(),
            }
          : a
      )
    );
  }, []);

  // Undo a trash: clear trashedAt and put the draft back in the active list.
  // The counterpart to trashAutosave retaining `data` — without this the
  // retained content has no way back out.
  // `timeUpdated` is bumped deliberately: clearing `trashedAt` alone would
  // LOWER getEffectiveUpdateTime below the trashed copy another tab still
  // holds, and the next cross-tab merge would re-trash the draft in both tabs.
  const restoreAutosave = useCallback((id: string) => {
    setAutosaves((prev) =>
      prev.map((a) =>
        a.id === id && !a.permanentlyDeletedAt
          ? {
              ...a,
              trashedAt: undefined,
              timeUpdated: Date.now(),
            }
          : a
      )
    );
  }, []);

  // Permanently delete an autosave by marking it with permanentlyDeletedAt.
  // (Note: the record remains as a tombstone and will be purged after a month.)
  const deleteAutosavePermanently = useCallback((id: string, bypassConfirmation?: string) => {
    const REQUIRED_CONFIRMATION = 'DELETE';

    // If bypass confirmation is provided, check if it matches the required text
    let confirmed = false;
    if (bypassConfirmation) {
      // Check if the bypass string contains the required confirmation
      confirmed = bypassConfirmation === 'PERMANENTLY DELETE ALL TRASHED AUTOSAVES';
    } else {
      // Show prompt for individual deletion
      const confirmation = window.prompt(
        "Type 'DELETE' to delete this draft for good. This destroys everything " +
          "you wrote in it and cannot be undone. Click Cancel to keep the draft."
      );
      confirmed = confirmation === REQUIRED_CONFIRMATION;

      if (!confirmed && confirmation !== null) {
        alert('That did not match, so the draft was not deleted.');
        return;
      }
    }

    if (confirmed) {
      setAutosaves((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                data: null,
                permanentlyDeletedAt: a.permanentlyDeletedAt || Date.now(),
              }
            : a
        )
      );
    }
  }, []);

  // Trash and permanently delete an autosave atomically (for programmatic cleanup).
  // If already trashed, preserves the original trashedAt timestamp.
  // Used internally for storage cleanup - no confirmation prompts.
  const trashAndDeleteAutosavePermanently = useCallback((id: string) => {
    setAutosaves((prev) =>
      prev.map((a) => (a.id === id ? trashAndDeleteAutosaveObj(a) : a))
    );
  }, []);

  // Clear all tombstone records (permanently deleted autosave IDs)
  const clearDeletedAutosaveIds = useCallback(() => {
    const clearTime = Date.now();
    setLastTombstoneClearTime(clearTime);
    setAutosaves((prev) =>
      prev.filter((autosave) => !autosave.permanentlyDeletedAt)
    );
  }, []);

  // Explicit, caseworker-initiated disposal of every draft on this device.
  // NOT wired to logout or session expiry, and it must not be (sgdev card
  // `ymp` entry `e03`) — losing a draft has to be something the worker chose.
  //
  // Order is load-bearing. The broadcast goes out BEFORE any storage is
  // touched, so a sibling tab drops its in-memory list before its next
  // persist can race us and rewrite the drafts into the store we are about
  // to empty.
  const removeAllDraftsFromThisDevice = useCallback(async () => {
    const wipeAt = Date.now();
    deviceWipeAtRef.current = wipeAt;
    // Pre-declare the emptied state as already persisted. The persist effect
    // is undebounced and fires on the state change below, mid-await: without
    // this it would write `{autosaves: []}` back through saveAutosaveStorage,
    // re-opening (and so re-creating) the database the clear is deleting, or
    // blocking the delete outright. Nothing of value is skipped — the write
    // it would perform is the empty state we are about to delete. A later
    // real draft differs from this string and persists normally.
    lastPersistedRef.current = JSON.stringify({
      autosaves: [],
      lastTombstoneClearTime: wipeAt,
    });
    // Active drafts, trashed drafts and tombstones all leave memory.
    setAutosaves([]);
    setLastTombstoneClearTime(wipeAt);

    try {
      localStorage.setItem(AUTOSAVE_WIPE_KEY, String(wipeAt));
    } catch {
      /* a tab we cannot reach is still better than not wiping */
    }
    try {
      localStorage.removeItem(LOCAL_STORAGE_AUTOSAVES_KEY);
    } catch {
      /* non-fatal */
    }

    try {
      // Attempted even in localStorage-fallback mode: a session that fell back
      // can be sitting on top of an IndexedDB store full of earlier drafts.
      await clearAllAutosaveStorage();
    } catch (err) {
      console.error('Device draft disposal failed:', err);
      // Failure path only — a disposal that quietly did not happen is the
      // worst outcome on this card. Counts and verdicts, no ids, no pathnames.
      logAutosaveHazardEvent(
        'device-draft-wipe-failed',
        'A caseworker asked to remove every draft from this device and the browser storage could not be cleared. Drafts may still be on the device.',
        { mode: idbMode ? 'indexeddb' : 'localstorage' }
      );
      throw err;
    }
  }, [idbMode]);

  // Debounce updates to avoid rapid-fire state changes.
  const debouncedUpdateAutosave = useMemo(
    () => debounce(updateAutosave, 500),
    [updateAutosave]
  );

  // Flush pending debounced updates on unmount.
  useEffect(() => {
    return () => {
      debouncedUpdateAutosave.flush();
    };
  }, [debouncedUpdateAutosave]);

  // Periodically purge autosaves that have been permanently deleted for over a month.
  useEffect(() => {
    const oneMonth = 30 * 24 * 60 * 60 * 1000;
    const interval = setInterval(() => {
      setAutosaves((prev) =>
        prev.filter(
          (a) =>
            !a.permanentlyDeletedAt ||
            Date.now() - a.permanentlyDeletedAt < oneMonth
        )
      );
    }, 60000); // Check every minute.
    return () => clearInterval(interval);
  }, []);

  // Archive all autosaves (a bulk "delete" action).
  const trashAllAutosavesMaybe = useCallback(() => {
    const confirmation = window.prompt(
      "Type 'TRASH ALL' to move all drafts to the trash. They keep their content there, and you can restore any of them until you permanently delete it."
    );
    if (confirmation === 'TRASH ALL') {
      setAutosaves((prev) =>
        prev.map((a) =>
          a.trashedAt ? a : { ...a, trashedAt: Date.now() }
        )
      );
    } else {
      alert('Confirmation text did not match. Autosaves not trashed.');
    }
  }, []);

  const autosaveContextValue: AutosaveContextValue = useMemo(
    () => ({
      addAutosave,
      autosaves,
      persistFailure,
      storagePersistence,
      debouncedUpdateAutosave,
      trashAllAutosavesMaybe,
      trashAutosave,
      restoreAutosave,
      deleteAutosavePermanently,
      trashAndDeleteAutosavePermanently,
      clearDeletedAutosaveIds,
      removeAllDraftsFromThisDevice,
      updateAutosave,
    }),
    [
      addAutosave,
      autosaves,
      persistFailure,
      storagePersistence,
      debouncedUpdateAutosave,
      trashAllAutosavesMaybe,
      trashAutosave,
      restoreAutosave,
      deleteAutosavePermanently,
      trashAndDeleteAutosavePermanently,
      clearDeletedAutosaveIds,
      removeAllDraftsFromThisDevice,
      updateAutosave,
    ]
  );

  return (
    <AutosaveContext.Provider value={autosaveContextValue}>
      {/* Hold children for the one initial IDB read (ms-scale, app root) so
          no form can race the store hydration and miss its draft. Fallback
          mode hydrates synchronously and renders immediately. */}
      {!idbMode || idbReady ? children : null}
      {/* Storage-full eviction: destroying trashed drafts that still hold
          their content is guarded by the same typed confirmation as a
          permanent delete. Cancel navigates nowhere. */}
      <AutosaveTypedConfirmDialog
        open={pendingEviction !== null}
        title="Storage for drafts on this device is full"
        warning="Clearing drafts out of the trash destroys everything written in them. This cannot be undone."
        details={
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {pendingEviction?.message ?? ''}
          </Typography>
        }
        confirmationRequired={EVICTION_CONFIRMATION_PHRASE}
        confirmLabel="Delete those trashed drafts for good"
        cancelLabel="Keep them — stay on this page"
        onConfirm={confirmPendingEviction}
        onCancel={cancelPendingEviction}
      />
    </AutosaveContext.Provider>
  );
};

export const useAutosaves = () => {
  const context = useContext(AutosaveContext);
  if (!context) {
    throw new Error('useAutosaves must be used within an AutosaveProvider');
  }
  return context;
};